import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { HtmlContent, stripHtml } from '@/components/ui/html-content';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTicket, useUpdateTicket, useAddTicketComment, useDeleteTicket } from '@/hooks/use-tickets';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useAuthStore } from '@/stores/auth-store';
import { apiClient } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  MessageSquare,
  Send,
  Clock,
  User,
  Trash2,
} from 'lucide-react';
import { cn, formatDistanceToNow } from '@/lib/utils';

const ticketTypeLabels: Record<string, string> = {
  bug: 'Bug',
  change_request: 'Change Request',
  general_inquiry: 'General Inquiry',
  approval_request: 'Approval Request',
};

const ticketTypeColors: Record<string, string> = {
  bug: 'bg-red-500/10 text-red-400',
  change_request: 'bg-blue-500/10 text-blue-400',
  general_inquiry: 'bg-purple-500/10 text-purple-400',
  approval_request: 'bg-yellow-500/10 text-yellow-400',
};

const statusColors: Record<string, string> = {
  open: 'bg-green-500/10 text-green-400',
  in_progress: 'bg-blue-500/10 text-blue-400',
  resolved: 'bg-purple-500/10 text-purple-400',
  closed: 'bg-gray-500/10 text-gray-400',
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-500/10 text-gray-400',
  medium: 'bg-blue-500/10 text-blue-400',
  high: 'bg-orange-500/10 text-orange-400',
  urgent: 'bg-red-500/10 text-red-400',
};

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const { data: ticketData, isLoading, isError, refetch } = useTicket(id!);
  const updateTicket = useUpdateTicket();
  const addComment = useAddTicketComment();
  const deleteTicket = useDeleteTicket();

  const ticket = ticketData?.data;
  useDocumentTitle(ticket?.subject ? `Ticket: ${ticket.subject}` : 'Ticket');

  const isStaff = user?.role !== 'client';

  const handleStatusChange = async (status: 'open' | 'closed' | 'in_progress' | 'resolved') => {
    try {
      await updateTicket.mutateAsync({ id: id!, status });
      toast({
        title: 'Status updated',
        description: `Ticket moved to ${status.replace('_', ' ')}.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to update status',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handlePriorityChange = async (priority: 'low' | 'medium' | 'high' | 'urgent') => {
    try {
      await updateTicket.mutateAsync({ id: id!, priority });
      toast({
        title: 'Priority updated',
        description: `Ticket priority set to ${priority}.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to update priority',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteTicket = async () => {
    if (!confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) return;
    try {
      await deleteTicket.mutateAsync(id!);
      toast({ title: 'Ticket deleted', variant: 'success' });
      navigate('/tickets');
    } catch (err: any) {
      toast({
        title: 'Failed to delete ticket',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      await addComment.mutateAsync({
        ticketId: id!,
        content: newComment.trim(),
        isInternal,
      });
      toast({
        title: 'Comment added',
        variant: 'success',
      });
      setNewComment('');
      setIsInternal(false);
    } catch (err: any) {
      toast({
        title: 'Failed to add comment',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  if (isError) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-red-400 mb-4">Failed to load ticket.</p>
          <button onClick={() => refetch()} className="btn-dyndev text-sm">
            Try Again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-pulse">
          <div className="h-4 w-32 rounded bg-[var(--border-color)]" />
          <div className="h-8 w-64 rounded bg-[var(--border-color)]" />
          <div className="h-32 rounded-lg bg-[var(--border-color)]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <h2 className="text-xl font-bold mb-2">Ticket not found</h2>
          <button onClick={() => navigate('/tickets')} className="text-dyndev-green hover:underline">
            Back to tickets
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Back button */}
      <button
        onClick={() => navigate('/tickets')}
        className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Tickets
      </button>

      {/* Ticket header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold">{ticket.subject}</h1>
          <Badge variant="outline" className={cn('text-xs', ticketTypeColors[ticket.type])}>
            {ticketTypeLabels[ticket.type] || ticket.type}
          </Badge>
          <Badge variant="outline" className={cn('text-xs', priorityColors[ticket.priority])}>
            {ticket.priority}
          </Badge>
          <Badge variant="outline" className={cn('text-xs', statusColors[ticket.status])}>
            {ticket.status?.replace('_', ' ')}
          </Badge>
        </div>
        {isStaff && (
          <button
            onClick={handleDeleteTicket}
            disabled={deleteTicket.isPending}
            className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors font-medium disabled:opacity-50"
          >
            {deleteTicket.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
            Delete Ticket
          </button>
        )}
      </div>

      {/* Meta info */}
      <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)] mb-6 flex-wrap">
        <span>#{ticket.id.slice(0, 8)}</span>
        <span className="flex items-center gap-1">
          <User size={12} />
          Submitted by {ticket.submitter?.name || 'Unknown'}
        </span>
        {ticket.assignee && (
          <span className="flex items-center gap-1">
            Assigned to {ticket.assignee.name}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {formatDistanceToNow(ticket.createdAt)}
        </span>
      </div>

      {/* Status & Priority management (staff only) */}
      {isStaff && (
        <div className="flex items-center gap-3 mb-6 p-3 rounded-lg bg-[var(--bg-secondary)] flex-wrap">
          <span className="text-sm text-[var(--text-secondary)]">Status:</span>
          <Select value={ticket.status} onValueChange={(v) => handleStatusChange(v as 'open' | 'closed' | 'in_progress' | 'resolved')}>
            <SelectTrigger className="w-[150px]" aria-label="Change ticket status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-sm text-[var(--text-secondary)]">Priority:</span>
          <Select value={ticket.priority} onValueChange={(v) => handlePriorityChange(v as 'low' | 'medium' | 'high' | 'urgent')}>
            <SelectTrigger className="w-[130px]" aria-label="Change ticket priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>

          {updateTicket.isPending && <Loader2 className="w-4 h-4 animate-spin text-dyndev-green" />}
        </div>
      )}

      {/* Description */}
      {ticket.description && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-sm">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <HtmlContent html={ticket.description} className="leading-relaxed" />
          </CardContent>
        </Card>
      )}

      {/* Comments section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageSquare size={14} />
            Comments ({ticket.comments?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Comment form */}
          <form onSubmit={handleSubmitComment} className="space-y-2 mb-6">
            <RichTextEditor
              value={newComment}
              onChange={setNewComment}
              placeholder="Add a comment…"
              minHeight="100px"
            />
            <div className="flex items-center justify-between">
              {isStaff && (
                <label className="flex items-center gap-2 cursor-pointer group select-none">
                  <Checkbox
                    checked={isInternal}
                    onCheckedChange={(checked) => setIsInternal(checked === true)}
                  />
                  <span className="text-xs text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">
                    Internal note (visible to staff only)
                  </span>
                </label>
              )}
              <Button
                type="submit"
                size="sm"
                className="gap-1"
                disabled={isSubmittingComment || addComment.isPending || !stripHtml(newComment)}>
                {isSubmittingComment ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                Send
              </Button>
            </div>
          </form>

          {/* Comments list */}
          {ticket.comments?.length > 0 ? (
            <div className="space-y-3">
              {ticket.comments.map((comment: any) => (
                <div
                  key={comment.id}
                  className="flex gap-3 p-3 rounded-lg bg-[var(--bg-secondary)]"
                >
                  <div className="w-8 h-8 rounded-full bg-dyndev-green/10 text-dyndev-green flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {comment.user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{comment.user?.name || 'Unknown'}</span>
                      <span className="text-[10px] text-[var(--text-secondary)]">
                        {formatDistanceToNow(comment.createdAt)}
                      </span>
                      {comment.isInternal && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                          Internal
                        </Badge>
                      )}
                    </div>
                    <HtmlContent html={comment.content} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-secondary)] text-center py-4">
              No comments yet. Be the first to respond.
            </p>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
